// Editing videos API
import { getFontFile } from "@/api/fonts.js";
import { getAPI, token, maxFileSize } from "@/api/params.js";
import { EDIT_TYPE } from "@/constants.js";

const LAYER_TYPE = Object.freeze({
  CONCAT: "concat",
  AUDIO: "audio",
  GRAPHICS: "graphics"
});

const ITEM_TYPE = Object.freeze({
  IMAGE: "image",
  SOUND: "sound",
  TEXT: "text",
  AUDIO: "atrim",
  GRAPHICS: "trim",
  CONCAT: "concat",
  CROP: "crop"
});

// Editing configurations
let configData = {
  layers: [],
  multistepPartDto: false
};
// Editing files (sounds, images, etc.)
let files = [];

/**
 * Make edit request using configData parameters
 * @param {Number} id Video ID
 */
async function editRequest(id) {
  const fd = new FormData();
  fd.append("config", JSON.stringify(configData));
  files.forEach(f => fd.append("files", f, f.name));

  const url = `${getAPI()}/video/${id}/edit?token=${token}`;
  const resp = await fetch(url, {
    method: "POST",
    body: fd,
    redirect: "follow",
    mode: "cors"
  });

  try {
    const respData = await resp.json();
    return respData;
  } catch (error) {
    console.log(error);
    console.warn(
      `Expected JSON response from ${url}, but HTTP code ${resp.status} was received - ${resp.statusText}`
    );
    return {
      success: resp.ok,
      error: resp.statusText
    };
  }
}

export async function getChannelsWhithCategory() {
  const getListCategory = await fetch(
    `${getAPI()}/mangomolo/list_channels?token=${token}&is_radio=false`
  );
  if (getListCategory) {
    const resp = await getListCategory.json();
    return resp.data;
  }
}

export async function getCategoryMM(id) {
  const getListCategory = await fetch(
    `${getAPI()}/mangomolo/categories_tree?token=${token}&channel_id=${id}&is_radio=false`
  );
  const resp = await getListCategory.json();
  return resp.data;
}

async function multiEditRequest(option) {
  const {
    deleteSourceBool,
    arabTitle,
    engTitle,
    descr,
    season,
    show,
    categoryId,
    intro,
    outro,
    introStart,
    outroStart,
    caskaderIntroOutro
  } = option;

  configData.tags = {
    title: arabTitle,
    second_lang_title: engTitle,
    description: descr,
    season,
    show,
    category: categoryId,
    intro,
    outro,
    introStart,
    outroStart,
    template: caskaderIntroOutro
  };

  const fd = new FormData();

  fd.append("config", JSON.stringify(configData));
  files.forEach(f => fd.append("files", f, f.name));
  const url = `${getAPI()}/video/multi_edit?token=${token}&delete_source=${deleteSourceBool}`;
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      Authorization: token
    },
    body: fd,
    redirect: "follow",
    mode: "cors"
  });

  try {
    const respData = await resp.json();
    return respData;
  } catch (error) {
    console.log(error);
    console.warn(
      `Expected JSON response from ${url}, but HTTP code ${resp.status} was received - ${resp.statusText}`
    );
    return {
      success: resp.ok,
      error: resp.statusText
    };
  }
}

/**
 * Add new layer to configData
 * @param {Object} layerData
 */
function addLayer(type, layerData, forceAdd = false) {
  if (!forceAdd) {
    for (let i = 0; i < configData.layers.length; i++) {
      if (configData.layers[i].type == type) {
        configData.layers[i].items = configData.layers[i].items.concat(
          layerData.items
        );
        return;
      }
    }
  }
  configData.layers.push({
    layerId: configData.layers.length,
    type: type,
    ...layerData
  });
}

/**
 * Add new file to files list
 * @param {File} file
 */
function addFile(file) {
  files.push(file);
}

/**
 * Erase configurations and files
 */
function resetSettings() {
  configData = {
    layers: []
  };
  files = [];
}

/**
 * Set cropping video configurations
 * @param {Object} cropData x, y, width and height (in pixels)
 */
function setCrop({ x, y, width, height }) {
  configData.crop = {
    x: x,
    y: y,
    width: width,
    height: height
  };
}

/**
 * @param {Array} mediaCuts Intervals multistep
 */

function multistepEditing({
  cuts,
  audio,
  templates,
  text,
  crop,
  multistepPartDto
}) {
  let baseIndex = 0;
  if (multistepPartDto) {
    configData.multistepPartDto = true;
  }

  if (audio) {
    setMainAudioGain(audio.mutePlayer ? 0 : 1);
  }
  cuts.forEach(cut => {
    if (cut.type == "inverse") {
      setConcat(
        cut.ranges.map(e => {
          return {
            start: e[0],
            end: e[1]
          };
        }),
        baseIndex,
        cut.source,
        audio,
        templates,
        text,
        crop,
        multistepPartDto
      );
    } else {
      setTrim(
        {
          start: cut.ranges[0][0],
          end: cut.ranges[0][1]
        },

        baseIndex,
        cut.source,
        audio,
        templates,
        text,
        crop,
        multistepPartDto
      );
    }
    baseIndex += cut.ranges.length;
  });
}

/**
 * Set trimming video configurations
 * @param {Object} cropData x, y, width and height (in pixels)
 */
function setTrim(
  { start, end },
  baseIndex,
  videoId,
  audio,
  templates,
  text,
  crop
) {
  const audioArray = [
    {
      start,
      end,
      type: ITEM_TYPE.AUDIO
    }
  ];
  const videoArray = [
    {
      start,
      end,
      type: ITEM_TYPE.GRAPHICS
    }
  ];
  if (audio != null) {
    if (audio.file) {
      audioArray.push(
        // audio
        {
          type: ITEM_TYPE.SOUND,
          source: files.length,
          gainLevel: 1,
          start: audio.start
        }
      );
      addFile(audio.file);
    }
  }
  if (templates != null) {
    videoArray.push({
      type: ITEM_TYPE.IMAGE,
      image: files.length,
      width: templates.width,
      height: templates.height,
      x: templates.x,
      y: templates.y,
      start: start,
      end: end
    });
    addFile(templates.file);
  }
  if (text != null) {
    videoArray.push({
      type: ITEM_TYPE.TEXT,
      content: text.text,
      font: getFontFile(text.font),
      fontSize: text.size,
      color: text.color,
      x: text.x,
      y: text.y,
      start: text.start,
      end: text.end
    });
  }
  if (crop != null) {
    configData[ITEM_TYPE.CROP] = {
      x: crop.x,
      y: crop.y,
      height: crop.height,
      width: crop.width
    };
  }

  addLayer(
    LAYER_TYPE.AUDIO,
    {
      concatPosition: baseIndex,
      videoSourceId: videoId,
      items: audioArray
    },
    true
  );

  addLayer(
    LAYER_TYPE.GRAPHICS,
    {
      concatPosition: baseIndex,
      videoSourceId: videoId,
      items: videoArray
    },
    true
  );
  // });
}

/**
 * Set concat video configurations
 * @param {Array} intervals Intervals to concat
 */
function setConcat(intervals, baseIndex, videoId) {
  intervals.map(([start, end], index) => {
    addLayer(
      LAYER_TYPE.AUDIO,
      {
        concatPosition: index + baseIndex,
        videoSourceId: videoId,
        items: [
          {
            start,
            end,
            type: ITEM_TYPE.AUDIO
          }
        ]
      },
      true
    ),
      addLayer(
        LAYER_TYPE.GRAPHICS,
        {
          concatPosition: index + baseIndex,
          videoSourceId: videoId,
          items: [
            {
              start,
              end,
              type: ITEM_TYPE.GRAPHICS
            }
          ]
        },
        true
      );
  });
  // addLayer(LAYER_TYPE.CONCAT, {
  //   items: intervals.map(([start, end]) => ({
  //     start,
  //     end,
  //     type: ITEM_TYPE.CONCAT, ////НЕ МОЕ
  //   }))
  // });
}

/**
 * Set overlay template picture
 * @param {Object} templateData position configs, timeline configs and file object
 */
function addTemplate({ x, y, width, height, start, end, file }) {
  addLayer(LAYER_TYPE.GRAPHICS, {
    items: [
      {
        type: ITEM_TYPE.IMAGE,
        image: files.length,
        width: width,
        height: height,
        x: x,
        y: y,
        start: start,
        end: end
      }
    ]
  });
  addFile(file);
}

/**
 * Decrease volume of origin video
 * @param {Number} level New volume in range [0:1]
 */
function setMainAudioGain(level) {
  configData.mainAudioGainLevel = level;
}

/**
 * Set sound
 * @param {Object} soundData start time in seconds and file object
 */
function addSound({ start, file }) {
  addLayer(LAYER_TYPE.AUDIO, {
    items: [
      {
        type: ITEM_TYPE.SOUND,
        source: files.length,
        gainLevel: 1,
        start: start
      }
    ]
  });
  addFile(file);
}

/**
 * Set text label
 * @param {Object} textData Position, font configs (family, size, color), timeline configs
 */
function addText({ text, font, size, color, x, y, start, end }) {
  addLayer(LAYER_TYPE.GRAPHICS, {
    items: [
      {
        type: ITEM_TYPE.TEXT,
        content: text,
        font: getFontFile(font),
        fontSize: size,
        color: color,
        x: x,
        y: y,
        start: start,
        end: end
      }
    ]
  });
}

/**
 * Edit video
 * @param {Number} id Video ID
 * @param {Object} data Edit configurations and files
 */
export const editVideo = async (id, data) => {
  resetSettings();
  const MB = 1024 * 1024;
  const audioBytes = data.templates?.file?.size ?? 0;
  const imageBytes = data.audio?.file?.size ?? 0;
  const totalSize = (audioBytes + imageBytes) / MB;

  if (totalSize > maxFileSize) {
    throw new Error("Files are too big");
  }

  if (EDIT_TYPE.CROP in data) {
    setCrop(data.crop);
  }

  if (EDIT_TYPE.TRIM in data) {
    setTrim(data.trim);
  }

  if (EDIT_TYPE.MULTISTEPEDIT in data) {
    multistepEditing(data.multistep);
  }

  if (EDIT_TYPE.CONCAT in data) {
    setConcat(data.concat.intervals);
  }

  if (EDIT_TYPE.AUDIO in data) {
    setMainAudioGain(data.audio.mutePlayer ? 0 : 1);

    if ("start" in data.audio) {
      addSound({
        start: data.audio.start,
        file: data.audio.file
      });
    }
  }

  if (EDIT_TYPE.TEXT in data) {
    addText(data.text);
  }

  if (EDIT_TYPE.TEMPLATES in data) {
    addTemplate(data.templates);
  }

  return await editRequest(id);
};

/**
 * Edit video
 * @param {Number} id Video ID
 * @param {Object} data Edit configurations and files
 */
export const multiEdit = async (
  id,
  data,
  stream,
  deleteSourceBool,
  arabTitle,
  engTitle,
  descr,
  season,
  show,
  categoryId,
  targetStream,
  intro,
  outro,
  introStart,
  outroStart,
  caskaderIntroOutro
) => {
  resetSettings();
  const MB = 1024 * 1024;
  const audioBytes = data.templates?.file?.size ?? 0;
  const imageBytes = data.audio?.file?.size ?? 0;
  const totalSize = (audioBytes + imageBytes) / MB;

  if (totalSize > maxFileSize) {
    throw new Error("Files are too big");
  }

  if (EDIT_TYPE.CROP in data) {
    setCrop(data.crop);
  }

  if (EDIT_TYPE.TRIM in data) {
    setTrim(data.trim);
  }

  if (EDIT_TYPE.MULTISTEPEDIT in data) {
    multistepEditing(data.multistep);
  }

  if (EDIT_TYPE.CONCAT in data) {
    setConcat(data.concat.intervals);
  }

  if (EDIT_TYPE.AUDIO in data) {
    setMainAudioGain(data.audio.mutePlayer ? 0 : 1);

    if ("start" in data.audio) {
      addSound({
        start: data.audio.start,
        file: data.audio.file
      });
    }
  }

  if (EDIT_TYPE.TEXT in data) {
    addText(data.text);
  }

  if (EDIT_TYPE.TEMPLATES in data) {
    addTemplate(data.templates);
  }

  return await multiEditRequest({
    deleteSourceBool,
    arabTitle,
    engTitle,
    descr,
    season,
    show,
    categoryId,
    intro,
    outro,
    introStart,
    outroStart,
    caskaderIntroOutro
  });
};
